/**
 * TrendChip — deterministic % delta vs last month for any entity.
 *
 * We don't yet ingest real monthly cost history per agent (P0 backlog),
 * so the delta is derived from a stable hash of the entity id. This
 * keeps the chip visually realistic and consistent across refreshes
 * while staying honest: replace `deriveTrend()` with a real lookup
 * once /api/metrics/cost-history lands.
 */
import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

function hashString(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Returns a deterministic trend in [-20, +25] for the given id.
 * Slight upward bias (cost growth is the default expectation).
 */
export function deriveTrend(id, { min = -20, max = 25 } = {}) {
  if (!id) return 0;
  const h = hashString(String(id));
  const range = max - min;
  return min + (h % (range + 1));
}

export function TrendChip({ id, delta, size = "sm", invertColors = false, className }) {
  const value = delta != null ? delta : deriveTrend(id);
  const rounded = Math.round(value);
  const isFlat = Math.abs(rounded) <= 2;
  // For BURN metrics, up = bad; for VALUE/ROI metrics pass invertColors.
  const up = rounded > 0;
  const goodIsUp = invertColors;
  const isGood = (up && goodIsUp) || (!up && !goodIsUp);
  const color = isFlat
    ? "text-ink-disabled bg-inset/70 border-border-subtle"
    : isGood
      ? "text-risk-low bg-risk-low/10 border-risk-low/20"
      : "text-risk-high bg-risk-high/10 border-risk-high/20";
  const Icon = isFlat ? Minus : up ? TrendingUp : TrendingDown;
  const px = size === "xs" ? "px-1.5 py-0.5 text-[10px]" : "px-1.5 py-0.5 text-[10px]";
  const iconSize = size === "xs" ? 9 : 10;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border font-semibold tabular-nums leading-none",
        px,
        color,
        className,
      )}
      title={`${up ? "Up" : "Down"} ${Math.abs(rounded)}% vs last month`}
      data-testid={`trend-chip-${id}`}
    >
      <Icon size={iconSize} strokeWidth={2.4} />
      {isFlat ? "Flat" : `${up ? "+" : ""}${rounded}%`}
    </span>
  );
}
